/**
 * Self-serve signup, once signed in: name the organisation, pass Turnstile,
 * and it is made with you as its first admin, on Free. Shown to somebody in
 * no organisation (where the invite-only app said "your admin adds you"), and
 * at /signup to somebody who wants another.
 *
 * While signup is shut -- the worker hands over no Turnstile site key --
 * this is the old empty state: an organisation's admin, or utuwrk, adds you.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { ORGANIZATION_NAME_LENGTH } from "@utuwrk/core";
import { api, sentence } from "../lib/api.js";
import { Turnstile, type TurnstileHandle } from "../lib/turnstile.js";
import { EmptyState, Link, Loading, PageHeader, Problem } from "./bits.js";

export function Signup({ hasOrg, onCreated }: { hasOrg: boolean; onCreated: (id: string) => Promise<void> }) {
  /** Undefined while asking; null while utuwrk is invite-only. */
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [passToken, setPassToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const check = useRef<TurnstileHandle>(null);

  useEffect(() => {
    let live = true;
    api
      .health()
      .then((report) => live && setSiteKey(report.signup ?? null))
      .catch(() => live && setSiteKey(null));
    return () => {
      live = false;
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!passToken) return;
    setBusy(true);
    setProblem(null);
    try {
      const { id } = await api.signup(name, passToken);
      await onCreated(id);
    } catch (failure) {
      setProblem(sentence(failure));
      check.current?.reset();
      setBusy(false);
    }
  }

  if (siteKey === undefined) return <Loading title="Your organisation" />;

  if (siteKey === null)
    return (
      <section className="page">
        <PageHeader title={hasOrg ? "Another organisation" : "You are not in an organisation yet"} />
        <EmptyState icon="inbox" title={hasOrg ? "Ask utuwrk" : "Nothing here yet"}>
          {hasOrg ? (
            <>utuwrk makes new organisations for now. Write to us and we will set one up.</>
          ) : (
            <>
              Your organisation's admin adds you, and your work appears here as soon as they do. Until then
              you can <Link to={{ name: "preview" }}>preview a template</Link>.
            </>
          )}
        </EmptyState>
      </section>
    );

  return (
    <section className="page">
      <PageHeader
        title={hasOrg ? "Another organisation" : "Set up your organisation"}
        meta="You are its first admin. It starts on Free, and you can change plan from Settings at any time."
      />
      <form className="panel admin-panel" onSubmit={(event) => void submit(event)}>
        <label className="field">
          Organisation name
          <input
            value={name}
            maxLength={ORGANIZATION_NAME_LENGTH}
            autoComplete="organization"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <Turnstile ref={check} siteKey={siteKey} action="signup" onToken={setPassToken} />
        <Problem>{problem}</Problem>
        <div className="action-row">
          <button className="button" type="submit" disabled={busy || !name.trim() || !passToken}>
            {busy ? "Setting up…" : "Create organisation"}
          </button>
        </div>
        {!hasOrg ? (
          <p className="muted">
            Joining a colleague's organisation instead? Their admin adds you, and it appears here.
          </p>
        ) : null}
      </form>
    </section>
  );
}
